import {
    useContext, useEffect, useMemo, useState 
} from 'react';
import {
    makeStyles 
} from '@mui/styles';
import {
    Box, Slide, Typography, useTheme 
} from '@mui/material';
import CardContainer from '../components/cardContainer';
import Card from '../components/card';
import ProgressBar from '../components/progressBar';
import {
    Choice, 
    DiagnosticAnswers
} from '../config/types';
import {
    FormContext 
} from '../../../contexts/form';
import {
    TypographyVariants, TypographyWeights 
} from '../../../figmaTheme';

type Props = {
    position: number,
    sectionKey: string,
    answerKey: string,
    title?: string,
    subtitle?: string,
    choices: Choice[],
    backJump?: number,
    forwardJump?: number
};

const useStyles = makeStyles(() => ({
    scrollableContainer: {
        overflow: 'scroll',
        scrollbarWidth: 'none',
        minHeight: '100%',
        width: '100vw',
        maxWidth: '692px',
        transform: 'translate(-24px, 0)',
        padding: '0 24px'
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        rowGap: '24px'
    },
    header: {
        fontWeight: TypographyWeights.MEDIUM,
        marginBottom: '8px'
    },
    choices: {
        display: 'flex',
        flexDirection: 'column',
        gridGap: '8px',
    },
    choice: {
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '8px',
        minHeight: '45px',
        padding: '16px',
        cursor: 'pointer',
        transition: 'background-color ease-in-out 0.25s'
    },
    choiceTitle: {
        fontWeight: TypographyWeights.BOLD,
        transition: 'color ease-in-out 0.25s'
    },
    choiceSubtitle: {
        transition: 'color ease-in-out 0.25s'
    }
}));

export const MultiSelectScreen = ({
    position,
    sectionKey,
    answerKey,
    title,
    subtitle,
    choices,
    backJump,
    forwardJump
}: Props): JSX.Element => {
    const formContext = useContext(FormContext);
    const answerChoices = formContext.answers[answerKey];
    const classes = useStyles();
    const theme = useTheme();
    const [choiceSelections, setChoiceSelections] = useState(choices.map((choice) => answerChoices?.includes(choice.key)));
    const [showButton, setShowButton] = useState(false);
    const overrideForwardJump = useMemo(() => {
        if (answerKey === 'focus_areas') return (formContext.answers as DiagnosticAnswers)?.totalDrinksBaseline === 0 ? 2 : 1;
        return forwardJump;
    }, [
        answerKey,
        (formContext.answers as DiagnosticAnswers)?.endGoal,
        forwardJump
    ]);
    const overrideBackJump = useMemo(() => {
        if (answerKey === 'focus_areas') return (formContext.answers as DiagnosticAnswers)?.endGoal === 'Drink Less' ? 3 : 4;
        return backJump;
    }, [
        answerKey,
        (formContext.answers as DiagnosticAnswers)?.endGoal,
        backJump
    ]);

    const section = useMemo(() => {
        return formContext?.sections.find(s => s.key === sectionKey);
    }, [sectionKey, formContext?.sections]);

    const toggleSelection = (e) => {
        const newChoiceSelections = choiceSelections;
        choiceSelections[e.currentTarget.dataset.index] = !choiceSelections[e.currentTarget.dataset.index];
        setChoiceSelections([...newChoiceSelections]);
    };

    useEffect(() => {
        const hasSelections = choiceSelections.find(selection => selection === true);
        setShowButton(!!hasSelections);
    }, [choiceSelections]);

    const onClick = () => {
        formContext.setAnswers({
            [answerKey]: choiceSelections.map((selection, index) => (
                selection && choices[index].key)
            ).filter(Boolean)
        });
        formContext.goNext(overrideForwardJump);
    };

    useEffect(() => {
        setChoiceSelections(choices.map((choice) => answerChoices?.includes(choice.key)));
    }, [answerChoices]);

    return (
        <CardContainer
            sectionKey={sectionKey}
            backJump={overrideBackJump}
            goBack={formContext.goBack}
            goNext={showButton && onClick}
            hasGradient
        >
            <Card>
                <ProgressBar
                    currentPosition={position}
                    sectionKey={sectionKey}
                />
                <Box className={classes.scrollableContainer}>
                    <Slide
                        in={true}
                        direction={formContext.screenDirection}
                        timeout={500}
                        mountOnEnter
                        unmountOnExit
                    >
                        <Box className={classes.content}>
                            {section?.title && (
                                <Typography
                                    variant={TypographyVariants.SMALL}
                                    className={classes.header}
                                    style={{
                                        color: section?.colors.main 
                                    }}
                                >
                                    {section.title}
                                </Typography>
                            )}
                            <Typography
                                variant={TypographyVariants.H3}
                                dangerouslySetInnerHTML={{
                                    __html: title 
                                }}
                            />
                            <Typography
                                variant={TypographyVariants.H6}
                                dangerouslySetInnerHTML={{
                                    __html: subtitle 
                                }}
                            />
                            <Box className={classes.choices}>
                                {choices.map((choice, index) => (
                                    <Box
                                        key={choice.key}
                                        data-index={index}
                                        className={classes.choice}
                                        style={{
                                            backgroundColor: choiceSelections[index] ? section?.colors.main : section?.colors.choice 
                                        }}
                                        onClick={toggleSelection}
                                    >
                                        {choice?.icon && (choice?.icon)}
                                        {choice?.title && (
                                            <Typography
                                                variant={TypographyVariants.H5}
                                                className={classes.choiceTitle}
                                                style={{
                                                    color: choiceSelections[index] ? theme.palette.white : section?.colors.choiceText 
                                                }}
                                            >
                                                {choice.title}
                                            </Typography>
                                        )}
                                        {choice?.subtitle && (
                                            <Typography
                                                variant={TypographyVariants.H6}
                                                className={classes.choiceSubtitle}
                                                style={{
                                                    color: choiceSelections[index] ? theme.palette.white : section?.colors.choiceText 
                                                }}
                                            >
                                                {choice.subtitle}
                                            </Typography>
                                        )}
                                    </Box>
                                ))}
                            </Box>
                            <Box
                                style={{
                                    height: '300px' 
                                }}
                            />
                        </Box>
                    </Slide>
                </Box>
            </Card>
        </CardContainer>
    );
};

export default MultiSelectScreen;
